import type { ParsedUrlQuery } from "querystring";

import { CURRENCY_URL_PARAM } from "@/features/display-currency/currency-url";
import { PRO_BUYER_URL_PARAM } from "@/features/pro-buyer-price/static/pro-buyer-price.static";
import type { SearchParams } from "@/features/search/api/queries";
import { US_ALL_ITEMS_URL_PARAM } from "@/features/us-market/us-shippable";
import { toContentLocale } from "@/i18n/config";

/**
 * URL <-> API param mapping.
 *
 * The URL query string is the single source of truth for search state. The
 * mapping is intentionally near-identity so state stays shareable and
 * crawlable, and so new facets need no code here:
 *
 *   - scalar strings (`query`, `sort`, ...) pass through as-is
 *   - `page` / `limit` and any `*_min` / `*_max` range bound coerce to numbers
 *   - everything else is a multi-select term facet -> string[]
 *
 * Facet keys line up with the API: a terms facet's `key` IS its query param
 * (`category`, `brand`, ...); a range facet's `key` maps to `<key>_min` /
 * `<key>_max` (`price` -> `price_min`/`price_max`).
 */

export const DEFAULT_LIMIT = 24;

const SCALAR_STRING_KEYS = [
  "query",
  "lang",
  "sort",
  "order",
  "search_type",
] as const;

const NUMBER_KEYS = ["page", "limit"] as const;

const isRangeKey = (key: string) =>
  key.endsWith("_min") || key.endsWith("_max");

/**
 * Routing plumbing Next.js injects into `router.query` — rewrite artefacts (`nxtP*`,
 * `nextInternalLocale`) that are not search state and must never reach the API.
 */
const isRouterInternalKey = (key: string) =>
  key === "nextInternalLocale" || key.startsWith("nxtP");

/**
 * Tracking params that ride along on inbound links but are not search state: campaign tags
 * (`utm_*`), ad click ids (`gclid`, `gbraid`, `fbclid`, ...) and the Iterable/Fresh-feed click
 * params (`ffuid`, `ff_item`, ...). They are neither sent to the API nor copied into the URLs the
 * UI writes, so an internal link never carries a tracking tag forward and one result set does
 * not fan out into one URL per campaign.
 */
const TRACKING_KEYS = [
  "ffuid",
  "ff_item",
  "ff_action",
  "ff_open_location",
  "gclid",
  "gbraid",
  "wbraid",
  "dclid",
  "gad_source",
  "gad_campaignid",
  "srsltid",
  "fbclid",
  "msclkid",
  "ttclid",
  "li_fat_id",
  "epik",
  "mc_cid",
  "mc_eid",
  "_gl",
];

export const isTrackingKey = (key: string) =>
  key.startsWith("utm_") || TRACKING_KEYS.includes(key);

/**
 * Param values are canonically lower-case, so one result set has exactly one URL.
 *
 * Facet values, slugs and enum tokens are all case-insensitive downstream — Typesense
 * filters against its lower-cased index — so the casing carries no meaning and only
 * fragments URLs for crawlers. The search term is the exception: those are the visitor's own
 * words, shown back in the search box. Router internals are rewrite artefacts, not search state.
 * Tracking values are opaque ids for someone else's system: a click id like `gclid` is
 * case-sensitive, and lower-casing it (WHO-6044) cut every paid PLP click loose from its campaign.
 */
const isCasePreservedKey = (key: string) =>
  key === "query" ||
  /* WHO-6199: `GBP`, as the middleware writes it; a 301 to `gbp` and its 302 back would loop. */
  key === CURRENCY_URL_PARAM ||
  isRouterInternalKey(key) ||
  isTrackingKey(key);

/** The canonical form of a URL param value. */
export const canonicalValue = (key: string, value: string) =>
  isCasePreservedKey(key) ? value : value.toLowerCase();

/**
 * Params the API reads a *single* value from — the scalars, numbers and range bounds
 * below, as opposed to a multi-select term facet. Repeating one is inert (`paramsFromQuery`
 * takes the first), so the canonical URL carries it once.
 */
export const isScalarKey = (key: string) =>
  (SCALAR_STRING_KEYS as readonly string[]).includes(key) ||
  (NUMBER_KEYS as readonly string[]).includes(key) ||
  isRangeKey(key);

const first = (value: string | string[] | undefined) =>
  Array.isArray(value) ? value[0] : value;

const toArray = (value: string | string[] | undefined) =>
  value == null ? [] : Array.isArray(value) ? value : [value];

/** Build the typed API params object from a Next.js router/query object. */
export const paramsFromQuery = (query: ParsedUrlQuery): SearchParams => {
  const params: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(query)) {
    if (
      value == null ||
      value === "" ||
      isRouterInternalKey(key) ||
      isTrackingKey(key) ||
      /* WHO-5743: the Pro Buyer toggle is URL state; `useSearchRoute` maps it for Pro Buyers only. */
      key === PRO_BUYER_URL_PARAM ||
      /* WHO-6510: the US "all items" switch is URL state; `resolveSearchRoute` reads it. */
      key === US_ALL_ITEMS_URL_PARAM ||
      /*
       * WHO-6199: the display currency is not a filter. It reaches the API as the query's own
       * `currency`, from `useDisplayCurrency` (seeded from it on first render), not as a facet.
       */
      key === CURRENCY_URL_PARAM
    ) {
      continue;
    }

    if ((SCALAR_STRING_KEYS as readonly string[]).includes(key)) {
      params[key] = first(value);
    } else if (
      (NUMBER_KEYS as readonly string[]).includes(key) ||
      isRangeKey(key)
    ) {
      const parsed = Number(first(value));

      if (!Number.isNaN(parsed)) {
        params[key] = parsed;
      }
    } else {
      params[key] = toArray(value);
    }
  }

  return params as SearchParams;
};

/**
 * Build the full API params from a URL query + locale. Used on both sides so
 * server prefetch and client hydration share one query key.
 */
export const buildSearchParams = (
  query: ParsedUrlQuery,
  locale?: string
): SearchParams => ({
  limit: DEFAULT_LIMIT,
  lang: toLang(locale),
  ...paramsFromQuery(query),
});

/**
 * Next's `locale` as the service's `lang`.
 *
 * The service publishes its accepted locales as an enum, so the generated type is a union while Next
 * types `locale` as a plain string. The service's own `LOCALES` are our six content languages, so
 * this goes through `toContentLocale` (`/en-us` asks for `en`, WHO-5766) and asserts rather than
 * validates. The service falls back to `en` for anything it doesn't know.
 */
export const toLang = (locale?: string) =>
  (locale === undefined
    ? undefined
    : toContentLocale(locale)) as SearchParams["lang"];

/**
 * Apply a patch to a query object, dropping keys whose value is `null`,
 * `undefined`, `""`, or `[]`. Returns a new plain object suitable for
 * `router.push({ query })` or serialization.
 *
 * Patched values are canonicalized here because every URL the UI writes goes through
 * this function — facet values arrive in whatever case the service indexed them
 * (`art_size: "LARGE"`), and writing that through would leave the site linking to URLs
 * the catch-all then redirects. Tracking params on the current URL are dropped for the
 * same reason: the link a click produces must not carry them forward. Nor does the display
 * currency (WHO-6199): internal links stay clean, and the middleware puts it back on a full load.
 */
export const patchQuery = (
  query: ParsedUrlQuery,
  patch: Record<string, string | string[] | number | null | undefined>
): ParsedUrlQuery => {
  const next: ParsedUrlQuery = {};

  for (const [key, value] of Object.entries(query)) {
    if (!isTrackingKey(key) && key !== CURRENCY_URL_PARAM) {
      next[key] = value;
    }
  }

  for (const [key, value] of Object.entries(patch)) {
    const empty =
      value == null || value === "" || (Array.isArray(value) && !value.length);

    if (empty) {
      delete next[key];
    } else if (Array.isArray(value)) {
      next[key] = value.map(entry => canonicalValue(key, entry));
    } else {
      next[key] =
        typeof value === "number" ? String(value) : canonicalValue(key, value);
    }
  }

  return next;
};

/**
 * Toggle one value of a multi-select term facet, returning the new value list.
 *
 * Matched case-insensitively, and the canonical form is what gets stored: `value` comes
 * from a service facet bucket in whatever case it was indexed (`art_size: "LARGE"`) while
 * the URL holds `large`, so comparing them raw would append the value again instead of
 * removing it — one click would tick a box that never unticks.
 */
export const toggleTermValue = (
  current: string | string[] | undefined,
  value: string
): string[] => {
  const list = toArray(current);
  const canonical = value.toLowerCase();
  const isSelected = (entry: string) => entry.toLowerCase() === canonical;

  return list.some(isSelected)
    ? list.filter(entry => !isSelected(entry))
    : [...list, canonical];
};

/**
 * A param string the service handed back — `meta.rules.params`, or `/resolve`'s answer — as a
 * router query object.
 *
 * It is already the URL's own vocabulary, so this only has to keep a param the service repeated
 * (`color=green&color=blue`) as a list. `Object.fromEntries` over the same iterator would keep the
 * last value alone and silently drop a filter the model found.
 */
export const urlQueryFromParams = (params: string): ParsedUrlQuery => {
  const query: ParsedUrlQuery = {};

  for (const [key, value] of new URLSearchParams(params)) {
    const existing = query[key];

    query[key] =
      existing === undefined
        ? value
        : [...(Array.isArray(existing) ? existing : [existing]), value];
  }

  return query;
};

/** Range facet key (`price`) -> its two query-param bounds. */
export const rangeKeys = (facetKey: string) => ({
  min: `${facetKey}_min`,
  max: `${facetKey}_max`,
});
